import toast from 'react-hot-toast';
import api from '../services/api.js';

/**
 * Imprime la cotización de un trámite en una vista limpia e institucional.
 * Puede recibir:
 *  - tramite: objeto trámite (consultará la API si no tiene items cargados)
 *  - datosModal: { items, cuantias, total, observaciones, descuentoMonto, descuentoDescripcion, repertorios }
 */
export const handleImprimirCotizacionTramite = async (tramite, datosModal = null) => {
  const printWindow = window.open('', '_blank', 'width=850,height=900');
  if (!printWindow) {
    toast.error('El navegador bloqueó la ventana emergente para imprimir. Habilite ventanas emergentes.');
    return;
  }

  let items = [];
  let cuantias = [];
  let total = 0;
  let descuentoMonto = 0;
  let descuentoDesc = '';
  let observaciones = '';
  let repertorios = tramite.repertorios || [];

  if (datosModal) {
    // Si viene directamente desde el modal de edición/creación de cotización
    items = datosModal.items || [];
    cuantias = datosModal.cuantias || [];
    total = datosModal.total || 0;
    descuentoMonto = datosModal.descuentoMonto || 0;
    descuentoDesc = datosModal.descuentoDescripcion || '';
    observaciones = datosModal.observaciones || '';
    if (datosModal.repertorios) {
      repertorios = datosModal.repertorios;
    }
  } else {
    // Si se invoca desde fuera con el objeto tramite
    let cotizacion = tramite.cotizacion;
    if (!cotizacion || !cotizacion.items || cotizacion.items.length === 0) {
      try {
        const res = await api.get(`/tramites/${tramite.id}/cotizacion`);
        if (res.data?.data) {
          cotizacion = res.data.data;
        }
      } catch (e) {
        console.warn('No se pudo cargar detalle completo de cotización:', e);
      }
    }

    if (!cotizacion) {
      printWindow.close();
      toast.error('El trámite no posee una cotización registrada aún.');
      return;
    }

    items = (cotizacion.items || []).filter(i => !i.is_cuantia);
    cuantias = (cotizacion.items || []).filter(i => i.is_cuantia);
    total = cotizacion.total || 0;
    descuentoMonto = cotizacion.descuento_monto || 0;
    descuentoDesc = cotizacion.descuento_descripcion || '';
    observaciones = cotizacion.observaciones || '';
  }

  // Filtrar solo los que tengan valor > 0 o cantidad válida
  const itemsFiltrados = items.filter(i => (parseInt(i.precio_unitario) > 0 || parseInt(i.monto) > 0));
  const cuantiasFiltradas = cuantias.filter(c => (parseInt(c.precio_unitario) > 0 || parseInt(c.monto) > 0 || (c.tipo_cuantia && c.descripcion)));

  const rowsItems = itemsFiltrados.map((it, idx) => {
    const cant = parseInt(it.cantidad) || 1;
    const precio = parseInt(it.precio_unitario) || 0;
    const subtotal = it.monto !== undefined ? parseInt(it.monto) : cant * precio;
    return `
      <tr>
        <td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; color: #475569;">${idx + 1}</td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; font-weight: 500; color: #1e293b;">
          ${it.descripcion || '-'}
        </td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; text-align: center; color: #475569;">
          ${cant}
        </td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; text-align: right; color: #475569;">
          $${new Intl.NumberFormat('es-CL').format(precio)}
        </td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; text-align: right; font-weight: 600; color: #0f172a;">
          $${new Intl.NumberFormat('es-CL').format(subtotal)}
        </td>
      </tr>
    `;
  }).join('');

  const rowsCuantias = cuantiasFiltradas.map((c, idx) => {
    const cant = parseInt(c.cantidad) || 1;
    const precio = parseInt(c.precio_unitario) || 0;
    const subtotal = c.monto !== undefined ? parseInt(c.monto) : cant * precio;
    return `
      <tr>
        <td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; color: #475569;">${idx + 1}</td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; font-weight: 500; color: #1e293b;">
          <span style="font-size: 11px; background: #e0f2fe; color: #0369a1; padding: 2px 6px; border-radius: 4px; margin-right: 6px; font-weight: 600;">CUANTÍA</span>
          ${c.tipo_cuantia ? `<strong>${c.tipo_cuantia}:</strong> ` : ''}${c.descripcion || '-'}
        </td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; text-align: center; color: #475569;">
          ${cant}
        </td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; text-align: right; color: #475569;">
          $${new Intl.NumberFormat('es-CL').format(precio)}
        </td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; text-align: right; font-weight: 600; color: #0f172a;">
          $${new Intl.NumberFormat('es-CL').format(subtotal)}
        </td>
      </tr>
    `;
  }).join('');

  const repertoriosHtml = repertorios.length > 0
    ? repertorios.map(r => {
        const contratoNombre = r.contrato?.nombre === 'Otro' ? r.contrato_otro : (r.contrato?.nombre || r.caratula || '');
        return `
        <span style="display: inline-block; background: #f8fafc; border: 1px solid #cbd5e1; border-radius: 6px; padding: 5px 12px; margin-right: 8px; margin-bottom: 6px; font-size: 12px;">
          <strong style="color: #0f172a;">Repertorio #${r.numero_repertorio}</strong>
          ${contratoNombre ? `<span style="background: #e0f2fe; color: #0369a1; font-weight: 700; padding: 2px 6px; border-radius: 4px; margin-left: 6px; font-size: 11px;">${contratoNombre}</span>` : ''}
        </span>
      `;
    }).join('')
    : (tramite.exento_repertorio
        ? `<span style="background: #ecfdf5; border: 1px solid #10b981; color: #065f46; font-weight: 700; padding: 4px 10px; border-radius: 4px; font-size: 11px;">🛡️ Exento Institucional (No requiere repertorio previo${tramite.motivo_exento_repertorio ? `: ${tramite.motivo_exento_repertorio}` : ''})</span>`
        : '<span style="color: #94a3b8; font-style: italic; font-size: 12px;">Sin repertorios asociados</span>');

  const html = `
    <!DOCTYPE html>
    <html lang="es">
      <head>
        <meta charset="utf-8">
        <title>Cotización Trámite ${tramite.numero_seguimiento || ''}</title>
        <style>
          @page {
            size: letter portrait;
            margin: 15mm;
          }
          body {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            color: #0f172a;
            margin: 0;
            padding: 30px;
            background: #fff;
            line-height: 1.45;
            font-size: 13px;
          }
          .header {
            text-align: center;
            margin-bottom: 25px;
            border-bottom: 2px solid #0f172a;
            padding-bottom: 15px;
          }
          .title {
            font-size: 20px;
            font-weight: 800;
            margin: 0 0 4px 0;
            text-transform: uppercase;
            letter-spacing: 0.8px;
            color: #0f172a;
          }
          .subtitle {
            font-size: 13px;
            color: #475569;
            text-transform: uppercase;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
          .top-badge-banner {
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: #f8fafc;
            border: 1px solid #cbd5e1;
            border-radius: 6px;
            padding: 10px 16px;
            margin-bottom: 20px;
            font-size: 12px;
          }
          .section-title {
            font-size: 13px;
            font-weight: 700;
            color: #0f172a;
            text-transform: uppercase;
            margin: 20px 0 10px 0;
            border-bottom: 1px solid #cbd5e1;
            padding-bottom: 5px;
            letter-spacing: 0.5px;
          }
          .info-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 15px;
          }
          .info-table th {
            width: 25%;
            text-align: left;
            padding: 7px 10px;
            background: #f1f5f9;
            color: #334155;
            font-weight: 600;
            font-size: 12px;
            border: 1px solid #e2e8f0;
            text-transform: uppercase;
          }
          .info-table td {
            padding: 7px 10px;
            border: 1px solid #e2e8f0;
            color: #0f172a;
            font-size: 13px;
          }
          .data-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 8px;
            margin-bottom: 20px;
          }
          .data-table th {
            text-align: left;
            padding: 9px 12px;
            background: #0f172a;
            color: #fff;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .total-card {
            background: #f8fafc;
            border: 2px solid #0f172a;
            border-radius: 8px;
            padding: 16px 20px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 25px;
          }
          .footer {
            text-align: center;
            font-size: 11px;
            color: #94a3b8;
            margin-top: 35px;
            border-top: 1px solid #e2e8f0;
            padding-top: 15px;
          }
          @media print {
            body { padding: 0; }
            .top-badge-banner { background: #fff !important; border: 1px solid #cbd5e1 !important; }
            .info-table th { background: #f1f5f9 !important; color: #0f172a !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .data-table th { background: #e2e8f0 !important; color: #0f172a !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="title">Conservador de Bienes Raíces de San Carlos</div>
          <div class="subtitle">Cotización Oficial de Derechos Arancelarios</div>
        </div>

        <div class="top-badge-banner">
          <div><strong>TRÁMITE N°:</strong> <span style="font-size: 14px; color: #0284c7; font-weight: 800;">${tramite.numero_seguimiento || 'S/N'}</span></div>
          <div><strong>FECHA EMISIÓN:</strong> ${new Date().toLocaleString('es-CL')}</div>
          <div><strong>ESTADO:</strong> <span style="text-transform: uppercase; font-weight: bold; color: #0369a1;">${tramite.estado?.nombre || 'Ingresado'}</span></div>
        </div>

        <div class="section-title">Datos del Solicitante y Trámite</div>
        <table class="info-table">
          <tr>
            <th>Solicitante</th>
            <td><strong>${tramite.nombre_solicitante || '-'}</strong></td>
            <th>RUT</th>
            <td><strong>${tramite.rut_solicitante || '-'}</strong></td>
          </tr>
          <tr>
            <th>Tipo Trámite</th>
            <td>${tramite.tipo_inscripcion?.nombre || tramite.tipo_inscripcion || '-'}</td>
            <th>Área / Registro</th>
            <td>${tramite.area?.nombre || 'Propiedad'}</td>
          </tr>
        </table>

        <div class="section-title">Repertorios Asociados</div>
        <div style="margin-bottom: 15px; padding: 4px 0;">
          ${repertoriosHtml}
        </div>

        <div class="section-title">Desglose de Derechos Arancelarios</div>
        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 35px;">#</th>
              <th>Descripción del Acto / Arancel</th>
              <th style="width: 70px; text-align: center;">Cant.</th>
              <th style="width: 120px; text-align: right;">Unitario</th>
              <th style="width: 130px; text-align: right;">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            ${rowsItems || ''}
            ${rowsCuantias || ''}
            ${!rowsItems && !rowsCuantias ? '<tr><td colspan="5" style="padding: 14px; text-align: center; color: #94a3b8; font-style: italic;">Sin ítems cotizados.</td></tr>' : ''}
          </tbody>
        </table>

        ${descuentoMonto > 0 ? `
          <div style="display: flex; justify-content: flex-end; gap: 20px; margin-bottom: 10px; font-size: 14px;">
            <span style="color: #64748b;">Descuento Aplicado (${descuentoDesc || 'Especial'}):</span>
            <span style="color: #dc2626; font-weight: 600;">-$${new Intl.NumberFormat('es-CL').format(descuentoMonto)}</span>
          </div>
        ` : ''}

        <div class="total-card">
          <span style="font-size: 15px; font-weight: 800; color: #0f172a; text-transform: uppercase;">Total Arancel a Pagar:</span>
          <span style="font-size: 24px; font-weight: 900; color: #0284c7;">$${new Intl.NumberFormat('es-CL').format(total)} CLP</span>
        </div>

        ${observaciones ? `
          <div class="section-title">Observaciones de la Cotización</div>
          <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; font-size: 13px; color: #334155; font-style: italic;">
            ${observaciones}
          </div>
        ` : ''}

        <div class="footer">
          Documento emitido por el Conservador de Bienes Raíces de San Carlos. Válido para pago de aranceles registrales.
        </div>

        <script>
          window.onload = function() {
            window.focus();
            window.print();
            setTimeout(function() { window.close(); }, 500);
          };
        </script>
      </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
};
